import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/components/AuthProvider";
import SignInPrompt from "@/pages/member/SignInPrompt";
import { IKO_KEYS, IKO } from "@/design-system";
import { ArrowLeft, Camera, Check, X, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api`, withCredentials: true });

/**
 * Vania Msg 217 §2 + Wave 15 Step 4 (3 Oct) — Submit-an-event wizard, 5 steps.
 * Reachable from: bottom-nav centre "New" button, every "submit an event" link.
 * Requires sign-in (the backend gate is `can_submit`, granted by default).
 *
 * Steps:
 *   1. Basics — poster, name, iko, date/time, venue
 *   2. Who's organising? — organiser handle (defaults to the member's handle),
 *      co-organiser handles chip list.
 *   3. Price + ticket link
 *   4. Visibility — public (default) or private; private shows an invitee picker.
 *   5. Description + preview + submit
 *
 * Done screen · if the response carries `self_kibanda_offer` the member can
 * start their 30-day kibanda trial in one tap (POST /api/member/kibanda/self-claim).
 */
export default function NewSubmit() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(1);
  const normalise = (h) => (h || "").trim().replace(/^@/, "").toLowerCase();
  const myHandle = normalise(user?.handle || (user?.email || "").split("@")[0] || "");
  const [form, setForm] = useState({
    title: "",
    iko: "",
    date: "",
    start_time: "",
    end_time: "",
    venue_name: "",
    location: "",
    is_free: false,
    price_amount: "",
    price_currency: "KES",
    ticket_link: "",
    description: "",
    organiser_handle: "",         // empty → falls back to myHandle on submit
    co_organiser_handles: [],     // chip list
    visibility: "public",
    invitees: [],                  // [{user_id, handle, display_name, picture}]
    file: null,
    filePreview: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const canNext = useMemo(() => {
    if (step === 1) return form.title && form.iko && form.date && form.start_time && form.venue_name;
    if (step === 2) return true;   // handles optional (fall back to self)
    if (step === 3) return true;
    if (step === 4) return true;
    if (step === 5) return form.description.length >= 20;
    return true;
  }, [step, form]);

  if (!loading && !user) {
    return <SignInPrompt title="submit an event"
                         line="every event goes through a human review before it goes live. sign in and take three minutes to add yours."
                         next="/app/new" testId="new-signin" />;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    set("file", f);
    set("filePreview", URL.createObjectURL(f));
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("iko", form.iko);
      fd.append("date", form.date);
      fd.append("start_time", form.start_time);
      if (form.end_time) fd.append("end_time", form.end_time);
      fd.append("venue_name", form.venue_name);
      if (form.location) fd.append("location", form.location);
      fd.append("is_free", form.is_free ? "true" : "false");
      if (!form.is_free && form.price_amount) {
        fd.append("price_amount", form.price_amount);
        fd.append("price_currency", form.price_currency);
      }
      fd.append("description", form.description);
      if (form.ticket_link) fd.append("ticket_link", form.ticket_link);
      const organiserHandle = normalise(form.organiser_handle) || myHandle;
      fd.append("organiser_handle", organiserHandle);
      if (form.co_organiser_handles.length) {
        fd.append("co_organiser_handles", form.co_organiser_handles.map(normalise).join(","));
      }
      fd.append("visibility", form.visibility);
      if (form.visibility === "private" && form.invitees.length) {
        fd.append("invited_user_ids", form.invitees.map((p) => p.user_id).join(","));
      }
      if (form.file) fd.append("file", form.file);
      const r = await client.post("/member/submit/multipart", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone({
        id: r.data.pending_event_id,
        dedupe_status: r.data.dedupe_status,
        self_kibanda_offer: r.data.self_kibanda_offer,
        organiser_handle: r.data.organiser_handle,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "submit failed. try again in a moment.");
    } finally { setBusy(false); }
  };

  if (done) {
    return <DoneScreen done={done} setDone={setDone} nav={nav} />;
  }

  return (
    <div className="flex h-full flex-col" data-testid="new-submit"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <header className="flex items-center justify-between px-5 pt-4">
        <button onClick={() => (step === 1 ? nav("/app") : setStep(step - 1))}
                data-testid="new-submit-back" aria-label="back"
                style={{ background: "transparent", color: "var(--ww-text)", border: 0, cursor: "pointer",
                         display: "flex", alignItems: "center", gap: 6,
                         fontFamily: "Arial", fontSize: 14 }}>
          <ArrowLeft size={18} /> back
        </button>
        <div className="flex items-center gap-2" data-testid="new-submit-progress">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} style={{ width: 10, height: 10, borderRadius: 999,
                                   background: n <= step ? "#FFD60A" : "var(--ww-line)" }} />
          ))}
        </div>
        <span style={{ width: 44 }} />
      </header>

      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-24" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 640 }}>
          <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 11, color: "var(--ww-text-muted)" }}>
            step {step} of 5
          </p>
          {step === 1 && (
            <>
              <H1>the basics</H1>
              <PosterField file={form.file} preview={form.filePreview} onChange={onFile} />
              <Field label="event name" v={form.title} on={(v) => set("title", v)} testId="new-title" />
              <IkoPick value={form.iko} onChange={(k) => set("iko", k)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="date" v={form.date} on={(v) => set("date", v)} type="date" testId="new-date" />
                <Field label="start time" v={form.start_time} on={(v) => set("start_time", v)} type="time" testId="new-start" />
              </div>
              <Field label="end time (optional)" v={form.end_time} on={(v) => set("end_time", v)} type="time" testId="new-end" />
              <Field label="venue name" v={form.venue_name} on={(v) => set("venue_name", v)} testId="new-venue" />
              <Field label="area (optional)" v={form.location} on={(v) => set("location", v)} testId="new-location" />
            </>
          )}
          {step === 2 && (
            <OrganiserStep form={form} set={set} myHandle={myHandle} normalise={normalise} />
          )}
          {step === 3 && (
            <>
              <H1>price & ticket</H1>
              <label className="flex items-center gap-2 my-3" style={{ fontFamily: "Arial", fontSize: 14 }}>
                <input type="checkbox" data-testid="new-is-free"
                       checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} />
                this event is free
              </label>
              {!form.is_free && (
                <div className="grid grid-cols-3 gap-2">
                  <Field label="currency" v={form.price_currency} on={(v) => set("price_currency", v.toUpperCase())} testId="new-cur" />
                  <div style={{ gridColumn: "span 2" }}>
                    <Field label="price" v={form.price_amount} on={(v) => set("price_amount", v)} testId="new-price" />
                  </div>
                </div>
              )}
              <Field label="ticket link (optional but recommended)" v={form.ticket_link}
                     on={(v) => set("ticket_link", v)} testId="new-ticket" />
            </>
          )}
          {step === 4 && (
            <VisibilityStep form={form} set={set} myHandle={myHandle} />
          )}
          {step === 5 && (
            <>
              <H1>tell the story · preview</H1>
              <p className="mb-3" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                minimum 20 characters. write like a friend · no marketing words.
              </p>
              <textarea data-testid="new-description" rows={6}
                         value={form.description} onChange={(e) => set("description", e.target.value)}
                         style={{ width: "100%", padding: 12, borderRadius: 12,
                                  background: "var(--ww-sunken)", border: "1px solid var(--ww-line)",
                                  color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
              <p className="mt-2" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
                {form.description.length} chars
              </p>
              <div className="rounded-2xl p-4 mt-4"
                   style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)" }}>
                {form.filePreview && (
                  <img src={form.filePreview} alt="" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 12 }} />
                )}
                <p className="mt-3" style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>
                  {form.iko || "iko"} · {form.visibility}
                </p>
                <h2 className="mt-1 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 22 }}>
                  {form.title.toLowerCase()}
                </h2>
                <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                  {form.date} · {form.start_time}{form.end_time ? ` – ${form.end_time}` : ""} · {form.venue_name.toLowerCase()}
                </p>
                <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                  by @{normalise(form.organiser_handle) || myHandle}
                  {form.co_organiser_handles.length > 0 && (
                    <> · with {form.co_organiser_handles.map((h) => `@${normalise(h)}`).join(", ")}</>
                  )}
                </p>
                <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                  {form.is_free ? "free" : (form.price_amount ? `${form.price_currency} ${form.price_amount}` : "price TBC")}
                </p>
                {form.visibility === "private" && (
                  <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                    invited: {form.invitees.length || "nobody yet"} {form.invitees.length === 1 ? "person" : "people"}
                  </p>
                )}
                <p className="mt-3" style={{ fontFamily: "Arial", fontSize: 14, whiteSpace: "pre-wrap" }}>
                  {form.description}
                </p>
              </div>
              {error && <p className="mt-3" style={{ color: "#FF9EBB", fontFamily: "Arial", fontSize: 13 }}>{error}</p>}
            </>
          )}
        </div>
      </div>

      <div style={{ position: "sticky", bottom: 0, background: "var(--ww-ground)",
                    borderTop: "1px solid var(--ww-line)", padding: 16 }}>
        <div className="mx-auto flex gap-3" style={{ maxWidth: 640 }}>
          <button data-testid="new-submit-next"
                  onClick={() => (step < 5 ? setStep(step + 1) : submit())}
                  disabled={!canNext || busy}
                  style={{ flex: 1, padding: "14px", borderRadius: 999,
                           background: canNext ? "#FFD60A" : "var(--ww-line)",
                           color: canNext ? "#1A1A30" : "var(--ww-text-muted)", border: 0,
                           fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                           textTransform: "lowercase", cursor: canNext ? "pointer" : "not-allowed" }}>
            {step < 5 ? "next" : (busy ? "sending…" : "send to review")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 · who's organising?
// ---------------------------------------------------------------------------
function OrganiserStep({ form, set, myHandle, normalise }) {
  const [showOther, setShowOther] = useState(!!form.organiser_handle);
  const [coDraft, setCoDraft] = useState("");

  const addCo = () => {
    const h = normalise(coDraft);
    if (!h) return;
    if (h === (normalise(form.organiser_handle) || myHandle)) {
      setCoDraft(""); return;
    }
    if (form.co_organiser_handles.map(normalise).includes(h)) {
      setCoDraft(""); return;
    }
    set("co_organiser_handles", [...form.co_organiser_handles, h]);
    setCoDraft("");
  };
  const removeCo = (h) =>
    set("co_organiser_handles", form.co_organiser_handles.filter((x) => normalise(x) !== normalise(h)));

  return (
    <>
      <H1>who's organising?</H1>
      <p className="mb-3" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
        curator is the host on the card. everyone can see this. you can tag co-curators too · they only land as "curating" once they accept. {/* ww-copy-ok: curator-code */}
      </p>

      <div className="rounded-2xl p-4"
           style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)" }}>
        <label className="flex items-start gap-3" style={{ cursor: "pointer" }}>
          <input type="radio" name="org" data-testid="new-org-me"
                 checked={!showOther}
                 onChange={() => { setShowOther(false); set("organiser_handle", ""); }}
                 style={{ marginTop: 3 }} />
          <div>
            <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15, textTransform: "lowercase" }}>
              me · @{myHandle}
            </div>
            <div style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
              you'll get a one-tap kibanda offer on the next screen.
            </div>
          </div>
        </label>
      </div>

      <div className="rounded-2xl p-4 mt-3"
           style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)" }}>
        <label className="flex items-start gap-3" style={{ cursor: "pointer" }}>
          <input type="radio" name="org" data-testid="new-org-other"
                 checked={showOther}
                 onChange={() => setShowOther(true)}
                 style={{ marginTop: 3 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15, textTransform: "lowercase" }}>
              someone else
            </div>
            <div style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
              type their handle · we'll create a lightweight listing they can claim later.
            </div>
            {showOther && (
              <input value={form.organiser_handle} onChange={(e) => set("organiser_handle", e.target.value)}
                     data-testid="new-org-handle" placeholder="handle (no @)"
                     style={{ width: "100%", padding: 10, marginTop: 10,
                              borderRadius: 10, background: "var(--ww-sunken)",
                              border: "1px solid var(--ww-line)",
                              color: "var(--ww-text)", fontFamily: "Arial",
                              fontSize: 14, outline: "none" }} />
            )}
          </div>
        </label>
      </div>

      <label className="mt-5 block" style={{ fontFamily: "Arial", textTransform: "uppercase",
             letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>
        co-curators (optional)
      </label>
      <div className="flex gap-2 mt-2">
        <input value={coDraft} onChange={(e) => setCoDraft(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCo(); } }}
               data-testid="new-co-input" placeholder="handle"
               style={{ flex: 1, padding: 10, borderRadius: 10,
                        background: "var(--ww-sunken)", border: "1px solid var(--ww-line)",
                        color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
        <button onClick={addCo} data-testid="new-co-add"
                style={{ padding: "10px 18px", borderRadius: 10, border: "1px solid var(--ww-line)",
                         background: "transparent", color: "var(--ww-text)",
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                         textTransform: "lowercase", cursor: "pointer" }}>
          add
        </button>
      </div>
      {form.co_organiser_handles.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="new-co-chips">
          {form.co_organiser_handles.map((h) => (
            <span key={h} style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                   padding: "6px 10px", borderRadius: 999,
                                   border: "1px solid var(--ww-line)", background: "var(--ww-surface)",
                                   color: "var(--ww-text)", fontFamily: "Arial", fontSize: 13 }}>
              @{normalise(h)}
              <button onClick={() => removeCo(h)} aria-label={`remove ${h}`}
                      style={{ background: "transparent", border: 0, cursor: "pointer",
                               color: "var(--ww-text-muted)", padding: 0, display: "inline-flex" }}>
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 4 · visibility + invitee picker
// ---------------------------------------------------------------------------
function VisibilityStep({ form, set, myHandle }) {
  return (
    <>
      <H1>who can see it?</H1>
      <p className="mb-3" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
        public shows on the main list. private is a quiet door · nobody sees it until you invite them and they accept.
      </p>

      {["public", "private"].map((v) => {
        const on = form.visibility === v;
        return (
          <label key={v} className="block rounded-2xl p-4 mb-3"
                 style={{ background: on ? "var(--ww-surface)" : "transparent",
                          border: `1px solid ${on ? "#FFD60A" : "var(--ww-line)"}`,
                          cursor: "pointer" }}>
            <div className="flex items-start gap-3">
              <input type="radio" name="vis" data-testid={`new-vis-${v}`}
                     checked={on} onChange={() => set("visibility", v)} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15, textTransform: "lowercase" }}>
                  {v === "public" ? "public" : "private · invite only"}
                </div>
                <div style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
                  {v === "public"
                    ? "anyone browsing watu wako can find it."
                    : "only people you invite · and who tap accept · can see it or share it."}
                </div>
              </div>
            </div>
          </label>
        );
      })}

      {form.visibility === "private" && (
        <InviteePicker form={form} set={set} myHandle={myHandle} />
      )}
    </>
  );
}

function InviteePicker({ form, set, myHandle }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await client.get("/people/search", { params: { q, limit: 10 } });
        setResults((r.data?.people || []).filter((p) => (p.handle || "").toLowerCase() !== myHandle));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q, myHandle]);

  const add = (p) => {
    if (form.invitees.find((x) => x.user_id === p.user_id)) return;
    set("invitees", [...form.invitees, p]);
    setQ(""); setResults([]);
  };
  const remove = (uid) => set("invitees", form.invitees.filter((x) => x.user_id !== uid));

  return (
    <div className="mt-4">
      <label className="block" style={{ fontFamily: "Arial", textTransform: "uppercase",
             letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>
        invite people
      </label>
      <input value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="search by handle or name" data-testid="new-invitee-search"
             style={{ width: "100%", padding: 10, marginTop: 4, borderRadius: 10,
                      background: "var(--ww-sunken)", border: "1px solid var(--ww-line)",
                      color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
      {q && (
        <div className="rounded-xl mt-2"
             style={{ background: "var(--ww-surface)", border: "1px solid var(--ww-line)", overflow: "hidden" }}>
          {searching && (
            <p className="px-3 py-2" style={{ color: "var(--ww-text-muted)",
               fontFamily: "Arial", fontSize: 12 }}>searching…</p>
          )}
          {!searching && results.length === 0 && (
            <p className="px-3 py-2" style={{ color: "var(--ww-text-muted)",
               fontFamily: "Arial", fontSize: 12 }}>no match. maybe they aren't on watu wako yet.</p>
          )}
          {results.map((p) => (
            <button key={p.user_id} onClick={() => add(p)}
                    data-testid={`new-invitee-result-${p.handle}`}
                    style={{ display: "flex", alignItems: "center", gap: 10,
                             width: "100%", padding: "10px 12px", textAlign: "left",
                             background: "transparent", border: 0, cursor: "pointer",
                             color: "var(--ww-text)" }}>
              <Avatar p={p} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14, textTransform: "lowercase" }}>
                  {(p.display_name || p.handle || "").toLowerCase()}
                </div>
                <div style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
                  @{p.handle}
                </div>
              </div>
              <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                             color: "#FFD60A", textTransform: "lowercase" }}>invite</span>
            </button>
          ))}
        </div>
      )}
      {form.invitees.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="new-invitee-chips">
          {form.invitees.map((p) => (
            <span key={p.user_id} style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                              padding: "6px 10px", borderRadius: 999,
                                              border: "1px solid var(--ww-line)", background: "var(--ww-surface)",
                                              color: "var(--ww-text)", fontFamily: "Arial", fontSize: 13 }}>
              @{p.handle}
              <button onClick={() => remove(p.user_id)} aria-label={`remove @${p.handle}`}
                      style={{ background: "transparent", border: 0, cursor: "pointer",
                               color: "var(--ww-text-muted)", padding: 0, display: "inline-flex" }}>
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="mt-3" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 12 }}>
        each person gets a private invite in their inbox. they only see the event after they accept.
      </p>
    </div>
  );
}

function Avatar({ p }) {
  const url = p.picture || "";
  if (url) {
    return <img src={url} alt="" style={{ width: 32, height: 32, borderRadius: 999, objectFit: "cover" }} />;
  }
  const letter = (p.handle || p.display_name || "?").slice(0, 1).toUpperCase();
  return (
    <div style={{ width: 32, height: 32, borderRadius: 999, background: "var(--ww-sunken)",
                   color: "var(--ww-text)", display: "grid", placeItems: "center",
                   fontFamily: "Poppins", fontWeight: 700, fontSize: 13 }}>{letter}</div>
  );
}

// ---------------------------------------------------------------------------
// Done screen · includes self-kibanda claim
// ---------------------------------------------------------------------------
function DoneScreen({ done, setDone, nav }) {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [claimError, setClaimError] = useState("");

  const claim = async () => {
    setClaiming(true); setClaimError("");
    try {
      const r = await client.post("/member/kibanda/self-claim",
                                   { handle: done.self_kibanda_offer.handle });
      setClaimed(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setClaimError(typeof detail === "string" ? detail : "couldn't start the trial. try the desk later.");
    } finally { setClaiming(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="new-submit-done"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10">
        <div className="mx-auto text-center" style={{ maxWidth: 520, marginTop: 60 }}>
          <div style={{ width: 64, height: 64, margin: "0 auto 16px", borderRadius: 999,
                        background: "#FFD60A", color: "#1A1A30",
                        display: "grid", placeItems: "center" }}>
            <Check size={30} strokeWidth={3} />
          </div>
          <h1 className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 30 }}>
            asante, sent to review
          </h1>
          <p className="mt-3" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 15 }}>
            a human will read it and, if it passes, it goes live on watu wako.
            {done.dedupe_status === "duplicate" && " looks like a similar event exists · we'll merge."}
          </p>

          {done.self_kibanda_offer && !claimed && (
            <div className="rounded-2xl p-5 mt-6 text-left" data-testid="new-self-kibanda-offer"
                 style={{ background: "var(--ww-surface)", border: "1px solid #FFD60A" }}>
              <div className="flex items-center gap-2 mb-2" style={{ color: "#FFD60A" }}>
                <Sparkles size={18} />
                <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                               textTransform: "uppercase", letterSpacing: "0.18em" }}>
                  one tap
                </span>
              </div>
              <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18, textTransform: "lowercase" }}>
                claim your kibanda · @{done.self_kibanda_offer.handle}
              </p>
              <p className="mt-2" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                {done.self_kibanda_offer.copy}
              </p>
              {claimError && (
                <p className="mt-2" style={{ color: "#FF9EBB", fontFamily: "Arial", fontSize: 13 }}>
                  {claimError}
                </p>
              )}
              <button onClick={claim} disabled={claiming}
                      data-testid="new-self-kibanda-claim"
                      style={{ marginTop: 14, padding: "12px 22px", borderRadius: 999,
                               background: "#FFD60A", color: "#1A1A30", border: 0,
                               fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                               textTransform: "lowercase",
                               cursor: claiming ? "wait" : "pointer" }}>
                {claiming ? "starting…" : "start free for 30 days"}
              </button>
            </div>
          )}

          {claimed && (
            <div className="rounded-2xl p-5 mt-6 text-left" data-testid="new-self-kibanda-claimed"
                 style={{ background: "var(--ww-surface)", border: "1px solid #FFD60A" }}>
              <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18, textTransform: "lowercase" }}>
                trial started · @{claimed.handle}
              </p>
              <p className="mt-2" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", fontSize: 13 }}>
                you're on 30 days free.{" "}
                {claimed.is_founding_organiser
                  ? `founding seat #${claimed.founding_seat_number} locked in.`
                  : "founding seats are gone, but you're on standard pricing after the trial."}
              </p>
            </div>
          )}

          <button onClick={() => nav("/app")} data-testid="new-submit-done-cta"
                  style={{ marginTop: 22, padding: "12px 24px", borderRadius: 999,
                           background: claimed || !done.self_kibanda_offer ? "#FFD60A" : "transparent",
                           color: claimed || !done.self_kibanda_offer ? "#1A1A30" : "var(--ww-text)",
                           border: `1px solid ${claimed || !done.self_kibanda_offer ? "#FFD60A" : "var(--ww-line)"}`,
                           fontFamily: "Poppins", fontWeight: 700, textTransform: "lowercase",
                           cursor: "pointer" }}>
            back to for you
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// shared presenters (unchanged from Msg 217)
// ---------------------------------------------------------------------------
function H1({ children }) {
  return (
    <h1 className="mt-2 mb-4 lowercase"
        style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 28, letterSpacing: "-0.025em" }}>
      {children}
    </h1>
  );
}
function Field({ label, v, on, type = "text", testId }) {
  return (
    <>
      <label className="mt-2 block" style={{ fontFamily: "Arial", textTransform: "uppercase",
             letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testId}
             style={{ width: "100%", padding: 12, marginTop: 4, marginBottom: 6,
                      borderRadius: 12, background: "var(--ww-sunken)", border: "1px solid var(--ww-line)",
                      color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none",
                      colorScheme: "dark" }} />
    </>
  );
}
function IkoPick({ value, onChange }) {
  return (
    <>
      <label className="mt-3 block" style={{ fontFamily: "Arial", textTransform: "uppercase",
             letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>iko (category)</label>
      <div className="mt-2 mb-2 flex flex-wrap gap-2">
        {IKO_KEYS.map((k) => {
          const on = value === k;
          const c = IKO[k]?.bright;
          return (
            <button key={k} data-testid={`new-iko-${k}`} type="button"
                    onClick={() => onChange(k)}
                    style={{ padding: "10px 16px", borderRadius: 999,
                             background: on ? "var(--ww-surface)" : "transparent",
                             color: on ? c : "var(--ww-text)",
                             border: `1px solid ${on ? c : "var(--ww-line)"}`,
                             fontFamily: "Poppins", fontWeight: 700, fontSize: 13, cursor: "pointer",
                             textTransform: "lowercase" }}>{IKO[k].label}</button>
          );
        })}
      </div>
    </>
  );
}
function PosterField({ file, preview, onChange }) {
  return (
    <>
      <label className="mt-2 block" style={{ fontFamily: "Arial", textTransform: "uppercase",
             letterSpacing: "0.22em", fontSize: 10, color: "var(--ww-text-muted)" }}>poster</label>
      <label htmlFor="new-poster-input" data-testid="new-poster-field"
             style={{ display: "flex", alignItems: "center", justifyContent: "center",
                      height: preview ? 260 : 140, marginTop: 4, marginBottom: 6, borderRadius: 12,
                      background: preview ? "transparent" : "var(--ww-sunken)",
                      backgroundImage: preview ? `url(${preview})` : "none",
                      backgroundSize: "cover", backgroundPosition: "center",
                      border: `1px dashed ${preview ? "transparent" : "var(--ww-line)"}`,
                      cursor: "pointer", position: "relative" }}>
        {!preview && (
          <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ww-text-muted)",
                         fontFamily: "Arial", fontSize: 13 }}>
            <Camera size={18} /> tap to add a poster
          </span>
        )}
      </label>
      <input id="new-poster-input" type="file" accept="image/*" onChange={onChange}
             style={{ display: "none" }} />
    </>
  );
}
